import { DockviewApi, DockviewPanelApi } from "dockview-react";
import React, { useCallback, useEffect } from "react";
import SafeNumberInput from "./SafeNumberInput";
import { api } from "./utils/api";
import { useReadAloud, ReadableItem } from "./utils/useReadAloud";
import { ReadAloudBar } from "./utils/ReadAloudBar";
import { cleanTextForSpeech } from "./utils/speechText";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { MdEdit } from "react-icons/md";
import { Link } from "react-router-dom";

interface IkkComment {
  id: number;
  subredditNamePrefixed: string;
  createdAt: string;
  body: string;
  score: number;
  permalink: string;
  mark: number;
  has_notes: boolean;
  // Matrix event_id of a Reddit-chat message (null for posts/comments)
  chatEventId: string | null;
}

const KK_CHAT_ROOM_URL = "https://www.reddit.com/chat/room/!HDsvId50SomfpHbiqEvKog%3Areddit.com";

interface IkkCommentsRequest {
  page: number;
  search_query: string;
  start_date: string;
  end_date: string;
  ascending: boolean;
  kind: "posts" | "chat";
  mark_filter: number | null; // null = Any
}

const DEFAULT_KK_COMMENT_REQUEST: IkkCommentsRequest = {
  page: 1,
  search_query: "",
  start_date: "2000-01-01",
  end_date: "2026-12-31",
  ascending: true,
  kind: "chat",
  mark_filter: null,
};

function formatCreatedAt(createdAt: string): string {
  const date = new Date(createdAt.replace(" ", "T"));
  if (isNaN(date.getTime())) return createdAt;
  const dayOfWeek = date.toLocaleDateString("en-US", { weekday: "short" });
  return `${createdAt} (${dayOfWeek})`;
}

function markColor(mark: number): string {
  switch (mark) {
    case 1:
      return "bg-blue-600";
    case 2:
      return "bg-yellow-500";
    default:
      return "bg-gray-400";
  }
}

const KK7766comments = (props: {
  api?: DockviewPanelApi;
  containerApi?: DockviewApi;
}) => {
  const [kkComments, setKkComments] = React.useState<IkkComment[]>([]);
  const [kkCommentRequest, setKkCommentRequest] = React.useState<IkkCommentsRequest>(() => {
    const stored = localStorage.getItem("kkCommentRequest");
    if (stored) {
      // merge so requests persisted before newer fields existed still work
      return { ...DEFAULT_KK_COMMENT_REQUEST, ...JSON.parse(stored) };
    }
    return DEFAULT_KK_COMMENT_REQUEST;
  });
  const [errorMessage, setErrorMessage] = React.useState("");
  const [currentReadIndex, setCurrentReadIndex] = React.useState(0);
  const readAloud = useReadAloud([kkComments]);

  const readableItems: ReadableItem[] = kkComments
    .slice(currentReadIndex)
    .map((comment) => ({
      key: String(comment.id),
      texts: [cleanTextForSpeech(comment.body)],
    }));

  const onItemDone = useCallback((key: string) => {
    const comment = kkComments.find((c) => String(c.id) === key);
    if (comment && comment.mark === 0) {
      markComment(comment.id, 0);
    }
    setCurrentReadIndex((prev) => prev + 1);
  }, [kkComments]);

  const handleCtlD = useCallback(() => {
    const firstUnmarked = kkComments.find((c) => c.mark === 0);
    if (firstUnmarked) {
      markComment(firstUnmarked.id, 0);
    }
  }, [kkComments]);

  useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      const isHotkeyCombo = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d";
      if (!isHotkeyCombo) return;
      if (props.containerApi && props.api && props.containerApi.activePanel?.id !== props.api.id) return;
      e.preventDefault();
      e.stopPropagation();
      (e as any).stopImmediatePropagation?.();
      handleCtlD();
    };
    window.addEventListener("keydown", keyHandler, { capture: true });
    return () => window.removeEventListener("keydown", keyHandler, { capture: true } as any);
  }, [props.api, props.containerApi, handleCtlD]);

  async function getKkComments(req: IkkCommentsRequest = kkCommentRequest) {
    setErrorMessage("");
    localStorage.setItem("kkCommentRequest", JSON.stringify(req));

    try {
      const resp = await api.get<IkkComment[]>("/kk-comments", {
        params: {
          page: req.page,
          search_query: req.search_query,
          start_date: req.start_date,
          end_date: req.end_date,
          ascending: req.ascending,
          kind: req.kind,
          // omit entirely when "Any" so FastAPI leaves mark_filter = None
          mark_filter: req.mark_filter ?? undefined,
        },
      });

      setKkComments(resp.data);

      props.api?.setTitle(`${req.kind === "chat" ? "Chat" : "Comments"}: ${req.search_query}`);
    } catch (error) {
      console.error(error);
      setErrorMessage("Error while fetching comments");
    }
  }

  function markComment(id: number, currentMark: number) {
    const newMark = (currentMark + 1) % 3;

    // Optimistic update
    setKkComments((prev) =>
      prev.map((c) => (c.id === id ? { ...c, mark: newMark } : c))
    );

    api
      .post("/kk-mark-comment", null, { params: { id, mark: newMark } })
      .catch((err) => {
        console.error("Failed to mark comment:", err);
        // Revert on error
        setKkComments((prev) =>
          prev.map((c) => (c.id === id ? { ...c, mark: currentMark } : c))
        );
      });
  }

  useEffect(() => {
    const handler = (e: Event) => {
      const { id, has_notes } = (e as CustomEvent<{ id: number; has_notes: boolean }>).detail;
      setKkComments((prev) =>
        prev.map((c) => (c.id === id ? { ...c, has_notes } : c))
      );
    };
    window.addEventListener("kk-note-changed", handler);
    return () => window.removeEventListener("kk-note-changed", handler);
  }, []);

  function openNoteEditor(comment: IkkComment) {
    if (!props.containerApi) return;
    addFloatingPanel(props.containerApi, "kk_comment_note", {
      id: comment.id,
    });
  }

  const renderImgurLink = (body: string) => {
    const imgurRegex =
      /https:\/\/i\.imgur\.com\/([a-zA-Z0-9]+)\.(png|jpg|jpeg|gif)/g;

    let matches: string[] = [];

    for (const match of body.match(imgurRegex) ?? []) {
      if (matches.indexOf(match) !== -1) continue;
      matches.push(match);
    }

    if (matches.length === 0) {
      return <></>;
    }

    return matches.map((match) => {
      const imgurId = match.split("/").pop();
      return (
        <img
          key={imgurId}
          src={`kk7766/img/${imgurId}`}
          alt="Imgur"
          className="h-auto w-1/2"
        />
      );
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto p-2">
      {!props.api && (
        <Link to="/mobile" className="btn btn-default mb-2 inline-block">
          To Menu
        </Link>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setCurrentReadIndex(0);
          getKkComments();
        }}
      >
        <div className="form-group flex items-center gap-3">
          <span
            className="flex items-center gap-2"
            title="Show imported chat messages or pre-existing posts"
          >
            {(["chat", "posts"] as const).map((k) => (
              <label key={k} className="flex items-center gap-1">
                <input
                  type="radio"
                  name="kk-kind"
                  value={k}
                  checked={kkCommentRequest.kind === k}
                  onChange={() => {
                    const next: IkkCommentsRequest = {
                      ...kkCommentRequest,
                      kind: k,
                      page: 1,
                    };
                    setCurrentReadIndex(0);
                    setKkCommentRequest(next);
                    getKkComments(next);
                  }}
                />
                {k === "chat" ? "Chat" : "Posts"}
              </label>
            ))}
          </span>

          <label htmlFor="mark-filter">Mark:</label>
          <select
            id="mark-filter"
            value={kkCommentRequest.mark_filter === null ? "" : String(kkCommentRequest.mark_filter)}
            onChange={(e) => {
              const v = e.target.value;
              const next: IkkCommentsRequest = {
                ...kkCommentRequest,
                mark_filter: v === "" ? null : Number(v),
                page: 1,
              };
              setCurrentReadIndex(0);
              setKkCommentRequest(next);
              getKkComments(next);
            }}
            className="border rounded p-1"
          >
            <option value="">Any</option>
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="2">2</option>
          </select>
        </div>

        <label htmlFor="page">Page:</label>
        <SafeNumberInput
          id="page"
          value={kkCommentRequest.page}
          onChange={(value) => setKkCommentRequest({ ...kkCommentRequest, page: value })}
          min={1}
        />

        <div className="form-group">
          <label htmlFor="search">Search:</label>
          <input
            type="text"
            id="search"
            value={kkCommentRequest.search_query}
            onChange={(e) => setKkCommentRequest({ ...kkCommentRequest, search_query: e.target.value })}
            className="border rounded p-1"
          />
        </div>

        <div className="form-group">
          <label htmlFor="start-date">Start date:</label>
          <input
            type="text"
            id="start-date"
            value={kkCommentRequest.start_date}
            onChange={(e) => setKkCommentRequest({ ...kkCommentRequest, start_date: e.target.value })}
            className="border rounded p-1"
          />
        </div>

        <div className="form-group">
          <label htmlFor="end-date">End date:</label>
          <input
            type="text"
            id="end-date"
            value={kkCommentRequest.end_date}
            onChange={(e) => setKkCommentRequest({ ...kkCommentRequest, end_date: e.target.value })}
            className="border rounded p-1"
          />
        </div>

        <button type="submit" className="hidden">
          Load Comments
        </button>
      </form>

      {errorMessage && <p className="text-red-500">{errorMessage}</p>}

      <div className="mb-20">
        {kkComments.map((comment) => (
          <div
            key={comment.id}
            id={String(comment.id)}
            className={`flex gap-3 mb-4 ${readAloud.currentlyReadingKey === String(comment.id) ? "bg-yellow-100" : ""}`}
          >
            {/* Mark button — same style as NewsSearch */}
            <div
              className={`min-w-5 cursor-pointer flex-shrink-0 ${markColor(comment.mark)}`}
              onClick={() => markComment(comment.id, comment.mark)}
            />

            <div className="flex-1 min-w-0">
              <p className="font-semibold">{formatCreatedAt(comment.createdAt)}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={comment.permalink}
                  className="flex flex-row gap-2"
                >
                  <span>{comment.subredditNamePrefixed}</span>
                  <span>(Score: {comment.score})</span>
                </a>
                <button
                  type="button"
                  title="Open note"
                  onClick={() => openNoteEditor(comment)}
                  className={`inline-flex items-center gap-1 text-xs px-1 rounded hover:bg-gray-100 ${
                    comment.has_notes ? "text-blue-600" : "text-gray-400"
                  }`}
                >
                  <MdEdit size={14} />
                  {comment.has_notes ? "note" : "add note"}
                </button>
                {comment.chatEventId && (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={KK_CHAT_ROOM_URL}
                    title={comment.chatEventId}
                    className="text-xs px-1 rounded text-gray-400 hover:bg-gray-100"
                  >
                    open chat
                  </a>
                )}
              </div>

              <div
                className="whitespace-pre-wrap kk-comment-body"
                dangerouslySetInnerHTML={{ __html: comment.body }}
              />

              {renderImgurLink(comment.body)}
            </div>
          </div>
        ))}
      </div>

      </div>

      <ReadAloudBar
        voiceControls={readAloud.voiceControls}
        setVoiceControls={readAloud.setVoiceControls}
        toggleReading={readAloud.toggleReading}
        skipItem={readAloud.skipItem}
        items={readableItems}
        onItemDone={onItemDone}
        idPrefix="kk-"
        containerApi={props.containerApi}
        panelApi={props.api}
      />
    </div>
  );
};

export default KK7766comments;
